import React, { useState, useEffect } from 'react';
import '../styles/ImageViewer.css';

function ImageViewer({ isOpen, imageUrl, onClose }) {
  const [isLoading, setIsLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Reset state when image changes
  useEffect(() => {
    if (isOpen && imageUrl) {
      setIsLoading(true);
      setImageError(false);
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [isOpen, imageUrl]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case '+':
        case '=':
          event.preventDefault();
          setZoom(prev => Math.min(prev * 1.2, 5));
          break;
        case '-':
          event.preventDefault();
          setZoom(prev => Math.max(prev / 1.2, 0.1));
          break;
        case '0':
          event.preventDefault();
          setZoom(1);
          setPosition({ x: 0, y: 0 });
          break;
        default:
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden'; // Prevent body scroll
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setImageError(true);
  };

  const handleWheel = (event) => {
    event.preventDefault();
    const delta = event.deltaY > 0 ? 0.9 : 1.1;
    setZoom(prev => Math.min(Math.max(prev * delta, 0.1), 5));
  };

  const handleMouseDown = (event) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: event.clientX - position.x,
        y: event.clientY - position.y
      });
    }
  };

  const handleMouseMove = (event) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: event.clientX - dragStart.x,
        y: event.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.2, 5));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev / 1.2, 0.1));
  };

  const handleResetZoom = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleDownload = async () => {
    try {
      // Extract filename from URL or use default
      const url = new URL(imageUrl, window.location.origin);
      const pathParts = url.pathname.split('/');
      const filename = pathParts[pathParts.length - 1] || 'image';
      const fileExtension = filename.includes('.') ? '' : '.png';
      const downloadName = filename + fileExtension;

      // Fetch the image as a blob to ensure proper download
      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error('Failed to fetch image');
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadName;
      link.style.display = 'none';
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback to simple download method
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = 'image';
      link.target = '_blank';
      link.click();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="image-viewer-overlay" onClick={onClose}>
      <div className="image-viewer-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="image-viewer-header">
          <div className="image-viewer-controls">
            <button 
              className="viewer-btn zoom-out" 
              onClick={handleZoomOut}
              disabled={zoom <= 0.1}
              title="Zoom Out (-)"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="M21 21l-4.35-4.35"/>
                <line x1="8" y1="11" x2="14" y2="11"/>
              </svg>
            </button>
            
            <span className="zoom-level">{Math.round(zoom * 100)}%</span>
            
            <button 
              className="viewer-btn zoom-in" 
              onClick={handleZoomIn}
              disabled={zoom >= 5}
              title="Zoom In (+)"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="M21 21l-4.35-4.35"/>
                <line x1="11" y1="8" x2="11" y2="14"/>
                <line x1="8" y1="11" x2="14" y2="11"/>
              </svg>
            </button>
            
            <button 
              className="viewer-btn reset-zoom" 
              onClick={handleResetZoom}
              title="Reset Zoom (0)"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            </button>
            
            <button 
              className="viewer-btn download" 
              onClick={handleDownload}
              title="Download Image"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                <polyline points="7,10 12,15 17,10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
            </button>
          </div>
          
          <button className="viewer-btn close" onClick={onClose} title="Close (Esc)">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Image Container */}
        <div 
          className="image-viewer-content"
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{ cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
        >
          {isLoading && (
            <div className="image-viewer-loading">
              <div className="loading-spinner">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3"/>
                  <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <p>Loading image...</p>
            </div>
          )}

          {imageError && (
            <div className="image-viewer-error">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <p>Failed to load image</p>
              <button className="retry-btn" onClick={() => {
                setImageError(false);
                setIsLoading(true);
              }}>
                Retry
              </button>
            </div>
          )}

          {!imageError && (
            <img
              src={imageUrl}
              alt="Fullscreen view"
              className="fullscreen-image"
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{
                transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                display: isLoading ? 'none' : 'block'
              }}
              draggable={false}
            />
          )}
        </div>

        {/* Footer with keyboard shortcuts */}
        <div className="image-viewer-footer">
          <div className="keyboard-shortcuts">
            <span>ESC: Close</span>
            <span>+/-: Zoom</span>
            <span>0: Reset</span>
            <span>Mouse wheel: Zoom</span>
            <span>Drag: Pan (when zoomed)</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImageViewer;
